// #kinship-quiz-chainpool-server — client hook for the precomputed
// chain pool. Fetches GET /api/public/quiz-chain-pool/:level once
// per level, caches forever (server cache + browser HTTP cache
// + React Query staleTime: Infinity).
//
// Replaces the prior at-mount L6 enumeration via useKinshipLabels
// that tripped the bridge's 30 req/min rate limit. The pool
// endpoint runs on a separate, more permissive 60/min limiter;
// the 6 GETs a fresh client issues at mount stay well under that
// cap. Repeat mounts hit the React Query cache, never the network.

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../client'
import type { CharacterGender, QuizAtom } from '../../lib/quizNames'

export interface PoolLabel {
  term: string
  isAgeDependent: boolean
  options?: string[]
}

export interface PoolEntryMutation {
  position: number
  atoms: QuizAtom[]
  labels: { male: PoolLabel; female: PoolLabel }
}

// #kinship-quiz-level-6-named-terms — L6 pool entries carry a
// named-term header for the 2-arrow chain visualisation. Present
// on level-6 entries only; absent for L1-L5.
export interface L6Metadata {
  namedKey: string
  directAtom: QuizAtom
  atomsByEgo: { male: QuizAtom[]; female: QuizAtom[] }
}

// #quiz-l7-ui — mirror of backend L7Metadata. Present only on L7
// entries. Per Kinship's locked contract: tier classifies the entry
// (1/2/3/4), symmetric+engineKey are set only for ITHARA_BANDUVU
// (the atom-less self-inverse special). `symmetric === true` →
// labels.male === labels.female and atoms is empty.
export interface L7Metadata {
  tier: 1 | 2 | 3 | 4
  symmetric?: boolean
  engineKey?: string
}

// #kinship-l7-name-gender-resolution — 4-slot inverse labels keyed
// on (B-gender × A-gender). Names imply gender at puzzle-render
// time, so A's gender is known once the A-figure has a name. The
// UI picks the slot whose key matches both genders to get a SINGULAR
// resolved Telugu term (no slash form to render). Mirror of backend
// L7Labels. Optional — pre-fix L7 entries (cache window during
// deploy crossover) may not carry this field; consumer should
// fall back to the slash-joined labels.male/female in that case.
export interface L7Labels {
  maleB_maleA:     PoolLabel
  maleB_femaleA:   PoolLabel
  femaleB_maleA:   PoolLabel
  femaleB_femaleA: PoolLabel
}

export interface PoolEntry {
  atoms: QuizAtom[]
  labels: { male: PoolLabel; female: PoolLabel }
  mutations: PoolEntryMutation[]
  l6?: L6Metadata
  l7?: L7Metadata
  l7Labels?: L7Labels
}

export interface PoolResponse {
  level:   number
  size:    number
  entries: PoolEntry[]
}

/** Lookup a label inside a pool entry by ego gender. Trivial
 *  accessor; lives here so callers don't have to remember the
 *  shape. */
export function labelForEgo(entry: PoolEntry, ego: CharacterGender): PoolLabel {
  return entry.labels[ego]
}

export function useChainPool(level: number): {
  entries: PoolEntry[]
  isLoading: boolean
  error: unknown
} {
  const { data, isLoading, error } = useQuery<PoolResponse>({
    queryKey: ['chain-pool', level],
    queryFn: async () => {
      const res = await apiClient.get<PoolResponse>(`/api/public/quiz-chain-pool/${level}`)
      return res.data
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  return {
    entries: data?.entries ?? [],
    isLoading,
    error,
  }
}
